import { deArray, type Vetor3 } from '../geometria/vetor3'
import type { EixoColuna } from '../geometria/anguloExtracao'
import { TIPOS_RISCO, type RiscoCena, type TipoRisco } from '../didatico/riscosCena'

export interface CenarioJson {
  id: string
  nome: string
  tipoAcidente: string
  cinematica: {
    direcaoImpacto: [number, number, number]
    transferenciaEnergia: number
  }
  veiculos: Array<{
    id: string
    tipoVeiculo: string
    caminhoModelo3d?: string
  }>
  vitimas: Array<{
    id: string
    eixoColuna: {
      baseColuna: [number, number, number]
      topoColuna: [number, number, number]
      frenteVitima: [number, number, number]
    }
  }>
  /** riscos da cena; ausente nos cenários que ainda não os declaram */
  riscos?: Array<{
    tipo: string
    posicao: [number, number, number]
    visivel?: boolean
  }>
}

export interface Cenario {
  id: string
  nome: string
  tipoAcidente: string
  direcaoImpacto: Vetor3
  transferenciaEnergia: number
  tipoVeiculo: string
  caminhoModelo3d?: string
  eixoColuna: EixoColuna
  /** o que a equipe precisa reconhecer antes de agir sobre o veículo */
  riscos: RiscoCena[]
}

export function normalizarCenario(json: CenarioJson): Cenario {
  const vitima = json.vitimas[0]
  const veiculo = json.veiculos[0]
  if (!vitima || !veiculo) throw new Error('O cenário precisa de um veículo e uma vítima.')

  return {
    id: json.id,
    nome: json.nome,
    tipoAcidente: json.tipoAcidente,
    direcaoImpacto: deArray(json.cinematica.direcaoImpacto),
    transferenciaEnergia: json.cinematica.transferenciaEnergia,
    tipoVeiculo: veiculo.tipoVeiculo,
    caminhoModelo3d: veiculo.caminhoModelo3d,
    eixoColuna: {
      baseColuna: deArray(vitima.eixoColuna.baseColuna),
      topoColuna: deArray(vitima.eixoColuna.topoColuna),
      frenteVitima: deArray(vitima.eixoColuna.frenteVitima),
    },
    riscos: normalizarRiscos(json.riscos),
  }
}

const ehTipoRisco = (tipo: string): tipo is TipoRisco =>
  (TIPOS_RISCO as readonly string[]).includes(tipo)

/**
 * Riscos declarados pelo cenário.
 *
 * Estavam no JSON desde o início — com tipo, posição, `visivel` e tudo — e a
 * normalização os descartava, então nenhuma tela os via. Um tipo que o catálogo
 * não conhece é IGNORADO em vez de derrubar o cenário: perder um risco é ruim,
 * mas não abrir a ocorrência é pior, e o painel mostra quantos foram lidos.
 */
function normalizarRiscos(riscos: CenarioJson['riscos']): RiscoCena[] {
  if (!riscos) return []
  return riscos
    .filter((r) => ehTipoRisco(r.tipo))
    .map((r) => ({
      tipo: r.tipo as TipoRisco,
      posicao: deArray(r.posicao),
      visivel: r.visivel ?? true,
    }))
}
